/**
 * Internal operator dashboard — HTTP calls (same auth as meetings API; internal-agenda gated on server).
 */

import type { InternalOperatorRealtimeEnvelopeV1 } from '@core/types/internalOperatorRealtime';

const viteEnv = (import.meta as { env?: Record<string, string | undefined> }).env ?? {};
const API_BASE_URL = viteEnv.VITE_API_URL ?? 'http://localhost:5000/api';

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('rithmo_token') : null;
  if (token) headers.Authorization = `Bearer ${token}`;
  if (API_BASE_URL.includes('ngrok-free.dev') || API_BASE_URL.includes('ngrok.io')) {
    headers['ngrok-skip-browser-warning'] = 'true';
  }
  return headers;
}

async function parseJson(res: Response): Promise<unknown> {
  return res.json().catch(() => ({}));
}

/** GET bootstrap envelope (INTERNAL_OPERATOR_REALTIME_V1). */
export async function fetchInternalOperatorBootstrap(
  meetingId: string
): Promise<InternalOperatorRealtimeEnvelopeV1> {
  const res = await fetch(
    `${API_BASE_URL}/meetings/${encodeURIComponent(meetingId)}/internal-operator-realtime`,
    { method: 'GET', headers: authHeaders() }
  );
  const body = (await parseJson(res)) as {
    success?: boolean;
    data?: { envelope: InternalOperatorRealtimeEnvelopeV1 };
    error?: { message?: string };
  };
  if (!res.ok) {
    throw new Error(body.error?.message ?? `HTTP ${res.status}`);
  }
  if (!body.success || !body.data?.envelope) {
    throw new Error('Invalid internal operator bootstrap response');
  }
  return body.data.envelope;
}

export async function postOperatorInterventionMode(
  meetingId: string,
  mode: 'auto' | 'manual' | 'hybrid' | null
): Promise<void> {
  const res = await fetch(
    `${API_BASE_URL}/meetings/${encodeURIComponent(meetingId)}/operator-intervention-mode`,
    { method: 'POST', headers: authHeaders(), body: JSON.stringify({ mode }) }
  );
  const body = (await parseJson(res)) as { success?: boolean; error?: { message?: string } };
  if (!res.ok || !body.success) {
    throw new Error(body.error?.message ?? `HTTP ${res.status}`);
  }
}

export async function postOperatorRecommendationResolution(
  meetingId: string,
  payload: {
    recommendationId: string;
    outcome: 'approved' | 'rejected' | 'dismissed' | 'remind_later';
    remindUntilMs?: number | null;
  }
): Promise<void> {
  const reqBody: Record<string, unknown> = {
    recommendationId: payload.recommendationId,
    outcome: payload.outcome,
  };
  if (payload.outcome === 'remind_later') {
    reqBody.remindUntilMs = payload.remindUntilMs ?? Date.now() + 60 * 60 * 1000;
  }
  const res = await fetch(
    `${API_BASE_URL}/meetings/${encodeURIComponent(meetingId)}/operator-recommendation-resolution`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(reqBody),
    }
  );
  const json = (await parseJson(res)) as { success?: boolean; error?: { message?: string } };
  if (!res.ok || !json.success) {
    throw new Error(json.error?.message ?? `HTTP ${res.status}`);
  }
}

export async function postLateStartResolution(
  meetingId: string,
  choice: 'compress' | 'keep_durations' | 'rithmo_suggests'
): Promise<void> {
  const res = await fetch(
    `${API_BASE_URL}/meetings/${encodeURIComponent(meetingId)}/operator-late-start-resolution`,
    { method: 'POST', headers: authHeaders(), body: JSON.stringify({ choice }) }
  );
  const body = (await parseJson(res)) as { success?: boolean; error?: { message?: string } };
  if (!res.ok || !body.success) {
    throw new Error(body.error?.message ?? `HTTP ${res.status}`);
  }
}

export async function postLateStartUndo(meetingId: string): Promise<void> {
  const res = await fetch(
    `${API_BASE_URL}/meetings/${encodeURIComponent(meetingId)}/operator-late-start-undo`,
    { method: 'POST', headers: authHeaders(), body: JSON.stringify({}) }
  );
  const body = (await parseJson(res)) as { success?: boolean; error?: { message?: string } };
  if (!res.ok || !body.success) {
    throw new Error(body.error?.message ?? `HTTP ${res.status}`);
  }
}

export async function postOperatorInternalItemLifecycleOverride(
  meetingId: string,
  internalAgendaItemId: string,
  lifecycleStatus:
    | 'not_started'
    | 'in_progress'
    | 'covered'
    | 'covered_weak_confidence'
    | 'at_risk'
    | null
): Promise<void> {
  const res = await fetch(
    `${API_BASE_URL}/meetings/${encodeURIComponent(meetingId)}/operator-internal-item-lifecycle-override`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ internalAgendaItemId, lifecycleStatus }),
    }
  );
  const body = (await parseJson(res)) as { success?: boolean; error?: { message?: string } };
  if (!res.ok || !body.success) {
    throw new Error(body.error?.message ?? `HTTP ${res.status}`);
  }
}
